import {useCallback, useMemo} from "react";
import {create} from "zustand";
import {persist} from "zustand/middleware";

import {zustandPersistStorage} from "@/src/modules/core/lib/zustand-custom-store";
import {flattenGroupedPermissions} from "@/src/modules/core/lib/permissions";

import type {Me} from "../types";

type MeStore = {
  me: Me | null;
  setMe: (me: Me | null) => void;
  clearMe: () => void;
};

export const useMeStore = create<MeStore>()(
  persist(
    (set) => ({
      me: null,

      setMe: (me: Me | null) => {
        set({
          me,
        });
      },

      clearMe: () => {
        set({
          me: null,
        });
      },
    }),
    {
      name: "me-store",
      storage: zustandPersistStorage,
      partialize: (state) => {
        return {me: state.me};
      },
    },
  ),
);

export function useStoredMe() {
  return useMeStore((state) => state.me);
}

export function useStoredMeProperties() {
  return useMeStore((state) => state.me?.properties ?? []);
}

export function useMePermissions() {
  const me = useStoredMe();
  return useMemo(() => {
    if (!me) return new Set<string>();
    if (me.effective_permissions) {
      return flattenGroupedPermissions(me.effective_permissions);
    }
    return new Set(me.role.permissions.map((p) => p.name));
  }, [me?.effective_permissions, me?.role?.permissions]);
}

export function useGetHasPermissions() {
  const permissions = useMePermissions();
  return useCallback(
    (permission: string) => permissions.has(permission),
    [permissions],
  );
}

const ACTIONS = ["store", "show", "update", "delete", "adjust"] as const;

export function useMeHasPermission(entity: string) {
  const getHasPermission = useGetHasPermissions();

  const permissions = ACTIONS.reduce(
    (acc, action) => {
      acc[action] = getHasPermission(`${entity} ${action}`);

      return acc;
    },
    {} as Record<(typeof ACTIONS)[number], boolean>,
  );

  return permissions;
}
